import { useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api.js';

/* ─── Types ─── */

interface ConnectionStatus {
  service: string;
  status: 'connected' | 'disconnected' | 'configured' | 'unknown';
  detail?: string;
}

interface CronWorkflow {
  workflow: string;
  status: 'ok' | 'error';
  error_message: string | null;
  details: string | null;
  timestamp: string;
  errors_7d: number;
}

interface CronHealthData {
  workflows: CronWorkflow[];
  recentErrors: Array<{ workflow: string; error_message: string; timestamp: string }>;
}

interface SyncRow {
  data_source: string;
  started_at: string;
  status: string;
  error_message: string | null;
  rows_processed: number | null;
}

interface ComplianceItem {
  id: string;
  label: string;
  status: 'ok' | 'warn' | 'fail';
  detail: string;
}

interface ComplianceStatus {
  items: ComplianceItem[];
  killSwitch: boolean;
  killSwitchAt: string | null;
  killSwitchReason: string | null;
}

/* ─── Helpers ─── */

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    connected: 'bg-green-100 text-green-700',
    ok: 'bg-green-100 text-green-700',
    success: 'bg-green-100 text-green-700',
    configured: 'bg-yellow-100 text-yellow-700',
    disconnected: 'bg-red-100 text-red-700',
    error: 'bg-red-100 text-red-700',
    failed: 'bg-red-100 text-red-700',
    unknown: 'bg-gray-100 text-gray-500',
  };
  const cls = colors[status] ?? colors.unknown;
  return (
    <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full ${cls}`}>
      {status}
    </span>
  );
}

/* ─── Connection loader ─── */

async function loadConnections(): Promise<ConnectionStatus[]> {
  const connections: ConnectionStatus[] = [];

  // SP-API per active account
  let activeAccounts: Array<{ id: string; name: string }> = [];
  try {
    const accts = await api.get<Array<{ id: string; name: string; is_active: number }>>('/accounts');
    activeAccounts = accts.filter((a) => a.is_active !== 0);
  } catch {
    connections.push({ service: 'SP-API', status: 'disconnected', detail: 'API offline' });
    return connections;
  }

  for (const acct of activeAccounts) {
    try {
      const sp = await api.get<{ configured: boolean; success?: boolean; error?: string }>(
        `/credentials/sp-api/${acct.id}/status`,
      );
      connections.push({
        service: `SP-API (${acct.name})`,
        status: sp.configured ? 'connected' : 'disconnected',
        detail: sp.configured ? (sp.success === false ? `Configured (${sp.error ?? 'test failed'})` : 'Connected') : 'No credentials',
      });
    } catch {
      connections.push({ service: `SP-API (${acct.name})`, status: 'unknown', detail: 'Could not check' });
    }
  }

  // 3-6: Parallel checks for Ads, Google, Telegram, Keepa
  const [adsResult, settingsResult, googleResult] = await Promise.allSettled([
    api.post<{ rows: Array<Record<string, number>> }>('/admin/query', {
      sql: "SELECT COUNT(*) as cnt FROM amazon_ads_profiles WHERE is_active = 1",
    }),
    api.get<Record<string, unknown>>('/settings'),
    api.post<{ rows: Array<Record<string, number>> }>('/admin/query', {
      sql: "SELECT COUNT(*) as cnt FROM google_connections WHERE is_active = 1",
    }),
  ]);

  // Ads API
  if (adsResult.status === 'fulfilled') {
    const cnt = (adsResult.value.rows?.[0]?.cnt ?? 0) as number;
    connections.push({ service: 'Ads API', status: cnt > 0 ? 'connected' : 'disconnected', detail: cnt > 0 ? `${cnt} active profiles` : 'No active profiles' });
  } else {
    connections.push({ service: 'Ads API', status: 'unknown', detail: 'Check failed' });
  }

  // Google Sheets (from google_connections table)
  if (googleResult.status === 'fulfilled') {
    const cnt = (googleResult.value.rows?.[0]?.cnt ?? 0) as number;
    connections.push({ service: 'Google Sheets', status: cnt > 0 ? 'connected' : 'disconnected', detail: cnt > 0 ? `${cnt} connection(s)` : 'Not configured' });
  } else {
    connections.push({ service: 'Google Sheets', status: 'unknown' });
  }

  // Settings-based: Telegram + Keepa
  if (settingsResult.status === 'fulfilled') {
    const settings = settingsResult.value;
    const notifications = settings?.notifications as Record<string, unknown> | undefined;
    const tg = notifications?.telegram as { enabled?: boolean; botToken?: string } | undefined;
    connections.push({
      service: 'Telegram',
      status: tg?.enabled && tg?.botToken ? 'connected' : tg?.botToken ? 'configured' : 'disconnected',
      detail: tg?.enabled && tg?.botToken ? 'Enabled' : tg?.botToken ? 'Token set, disabled' : 'Not configured',
    });

  } else {
    connections.push({ service: 'Telegram', status: 'unknown' });
  }

  // Keepa: check if any account has keepa key (accounts table has has_keepa_key flag)
  try {
    const keepaRes = await api.post<{ rows: Array<Record<string, number>> }>('/admin/query', {
      sql: "SELECT COUNT(*) as cnt FROM accounts WHERE has_keepa_key = 1 AND is_active = 1",
    });
    const cnt = (keepaRes.rows?.[0]?.cnt ?? 0) as number;
    connections.push({ service: 'Keepa', status: cnt > 0 ? 'connected' : 'disconnected', detail: cnt > 0 ? `${cnt} account(s) with key` : 'No API key' });
  } catch {
    connections.push({ service: 'Keepa', status: 'unknown' });
  }

  return connections;
}

/* ─── Component ─── */

export function SystemHealth() {
  const [connections, setConnections] = useState<ConnectionStatus[]>([]);
  const [cronHealth, setCronHealth] = useState<CronHealthData | null>(null);
  const [syncHistory, setSyncHistory] = useState<SyncRow[]>([]);
  const [compliance, setCompliance] = useState<ComplianceStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [conns, cron, sync, comp] = await Promise.all([
        loadConnections(),
        api.get<CronHealthData>('/admin/cron-health').catch(() => null),
        api.post<{ results: SyncRow[] }>('/admin/query', {
          sql: `SELECT sh.data_source, sh.started_at, sh.status, sh.error_message, sh.rows_processed
                FROM sync_history sh ORDER BY sh.started_at DESC LIMIT 20`,
        }).then((r) => r.results ?? []).catch(() => []),
        api.get<ComplianceStatus>('/compliance-status').catch(() => null),
      ]);
      setConnections(conns);
      setCronHealth(cron);
      setSyncHistory(sync);
      setCompliance(comp);
    } catch (e: any) {
      setError(e.message ?? 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  async function handleEmergencyStop() {
    const reason = prompt('Reason for emergency stop (optional):');
    if (reason === null) return;
    await api.post('/emergency-stop', { reason });
    refresh();
  }

  async function handleReenable() {
    if (!confirm('Re-enable all automation? Cron jobs will resume on next trigger.')) return;
    const settings = await api.get<Record<string, unknown>>('/settings');
    await api.put('/settings', { ...settings, killSwitch: false, killSwitchAt: undefined, killSwitchReason: undefined });
    refresh();
  }

  useEffect(() => { refresh(); }, [refresh]);

  const serviceIcons: Record<string, string> = {
    API: '🌐',
    Accounts: '👤',
    'Ads API': '📢',
    'Google Sheets': '📊',
    Telegram: '💬',
    Keepa: '📈',
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900">System Health</h1>
        <button
          onClick={refresh}
          disabled={loading}
          className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 transition"
        >
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* ─── Connection Status ─── */}
      <section>
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Connections</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {connections.map((c) => {
            const icon = Object.entries(serviceIcons).find(([k]) => c.service.startsWith(k))?.[1]
              ?? (c.service.startsWith('SP-API') ? '🔗' : '⚙️');
            return (
              <div
                key={c.service}
                className="rounded-xl border border-gray-200 bg-white px-4 py-3 flex flex-col gap-1"
              >
                <div className="flex items-center gap-2">
                  <span className="text-base">{icon}</span>
                  <span className="text-sm font-medium text-gray-700 truncate">{c.service}</span>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={c.status} />
                </div>
                {c.detail && <p className="text-xs text-gray-400 truncate">{c.detail}</p>}
              </div>
            );
          })}
          {connections.length === 0 && !loading && (
            <p className="col-span-full text-sm text-gray-400">No connection data available.</p>
          )}
        </div>
      </section>

      {/* ─── BSA Compliance ─── */}
      {compliance && (
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
              Amazon BSA Compliance
            </h2>
            {compliance.killSwitch ? (
              <button
                onClick={handleReenable}
                className="rounded-lg px-3 py-1.5 text-xs font-medium bg-green-600 text-white hover:bg-green-700"
              >
                Re-enable Automation
              </button>
            ) : (
              <button
                onClick={handleEmergencyStop}
                className="rounded-lg px-3 py-1.5 text-xs font-medium bg-red-600 text-white hover:bg-red-700"
              >
                Emergency Stop
              </button>
            )}
          </div>
          {compliance.killSwitch && (
            <div className="mb-3 rounded-lg border border-red-300 bg-red-50 p-3">
              <p className="text-sm font-semibold text-red-800">KILL SWITCH ACTIVE</p>
              <p className="text-xs text-red-700 mt-0.5">
                All automation halted since {compliance.killSwitchAt?.slice(0, 16)}.
                {compliance.killSwitchReason && <> Reason: {compliance.killSwitchReason}</>}
              </p>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {compliance.items.map(item => (
              <div key={item.id} className={`rounded-xl border p-3 ${
                item.status === 'ok' ? 'border-green-200 bg-green-50' :
                item.status === 'warn' ? 'border-amber-200 bg-amber-50' :
                'border-red-200 bg-red-50'
              }`}>
                <div className="flex items-start gap-2">
                  <span className={`mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-full text-xs font-bold ${
                    item.status === 'ok' ? 'bg-green-600 text-white' :
                    item.status === 'warn' ? 'bg-amber-500 text-white' :
                    'bg-red-600 text-white'
                  }`}>
                    {item.status === 'ok' ? '✓' : item.status === 'warn' ? '!' : '✗'}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${
                      item.status === 'ok' ? 'text-green-900' :
                      item.status === 'warn' ? 'text-amber-900' :
                      'text-red-900'
                    }`}>{item.label}</p>
                    <p className="text-xs text-gray-600 mt-0.5">{item.detail}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ─── Cron Workflow Health ─── */}
      <section>
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
          Cron Workflow Health
        </h2>
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
          {!cronHealth || cronHealth.workflows.length === 0 ? (
            <p className="px-4 py-6 text-sm text-gray-400 text-center">
              No cron data yet -- health will populate after the next scheduled run.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    <th className="text-left px-4 py-2 font-medium text-gray-500">Workflow</th>
                    <th className="text-left px-4 py-2 font-medium text-gray-500">Status</th>
                    <th className="text-left px-4 py-2 font-medium text-gray-500">Last Run</th>
                    <th className="text-left px-4 py-2 font-medium text-gray-500">Details</th>
                    <th className="text-right px-4 py-2 font-medium text-gray-500">7d Errors</th>
                  </tr>
                </thead>
                <tbody>
                  {cronHealth.workflows.map((w) => (
                    <tr key={w.workflow} className="border-b border-gray-50 hover:bg-gray-50/50">
                      <td className="px-4 py-2 font-medium text-gray-700">{w.workflow}</td>
                      <td className="px-4 py-2">
                        <StatusBadge status={w.status} />
                      </td>
                      <td className="px-4 py-2 text-gray-500" title={w.timestamp}>
                        {timeAgo(w.timestamp)}
                      </td>
                      <td className="px-4 py-2 text-gray-500 max-w-xs truncate" title={w.error_message || w.details || ''}>
                        {w.error_message ? (
                          <span className="text-red-600">{w.error_message}</span>
                        ) : (
                          w.details || '--'
                        )}
                      </td>
                      <td className="px-4 py-2 text-right">
                        {w.errors_7d > 0 ? (
                          <span className="inline-block px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded-full">
                            {w.errors_7d}
                          </span>
                        ) : (
                          <span className="text-gray-400">0</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Recent Errors (always visible) */}
        {cronHealth && (
          <div className="mt-4 rounded-xl border border-gray-200 bg-white overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-600">
                Recent Errors (24h)
                {cronHealth.recentErrors.length > 0 && (
                  <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded-full">
                    {cronHealth.recentErrors.length}
                  </span>
                )}
              </h3>
            </div>
            {cronHealth.recentErrors.length === 0 ? (
              <p className="px-4 py-4 text-sm text-gray-400">No errors in the last 24 hours.</p>
            ) : (
              <div className="divide-y divide-gray-50 max-h-64 overflow-y-auto">
                {cronHealth.recentErrors.map((e, i) => (
                  <div key={i} className="px-4 py-2 flex items-start gap-3 text-sm">
                    <span className="text-gray-400 whitespace-nowrap text-xs pt-0.5" title={e.timestamp}>
                      {timeAgo(e.timestamp)}
                    </span>
                    <span className="font-medium text-gray-600 whitespace-nowrap">{e.workflow}</span>
                    <span className="text-red-600 truncate">{e.error_message}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </section>

      {/* ─── Sync History ─── */}
      <section>
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
          Recent Sync History
        </h2>
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
          {syncHistory.length === 0 ? (
            <p className="px-4 py-6 text-sm text-gray-400 text-center">No sync history entries.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    <th className="text-left px-4 py-2 font-medium text-gray-500">Data Source</th>
                    <th className="text-left px-4 py-2 font-medium text-gray-500">Time</th>
                    <th className="text-left px-4 py-2 font-medium text-gray-500">Status</th>
                    <th className="text-right px-4 py-2 font-medium text-gray-500">Rows</th>
                    <th className="text-left px-4 py-2 font-medium text-gray-500">Error</th>
                  </tr>
                </thead>
                <tbody>
                  {syncHistory.map((s, i) => (
                    <tr key={i} className="border-b border-gray-50 hover:bg-gray-50/50">
                      <td className="px-4 py-2 font-medium text-gray-700">{s.data_source}</td>
                      <td className="px-4 py-2 text-gray-500" title={s.started_at}>
                        {timeAgo(s.started_at)}
                      </td>
                      <td className="px-4 py-2">
                        <StatusBadge status={s.status} />
                      </td>
                      <td className="px-4 py-2 text-right text-gray-500">
                        {s.rows_processed != null ? s.rows_processed.toLocaleString() : '--'}
                      </td>
                      <td className="px-4 py-2 text-red-600 max-w-xs truncate" title={s.error_message || ''}>
                        {s.error_message || ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
